import { Component, OnDestroy, OnInit } from '@angular/core'
import { collection, collectionData, limit, orderBy, query, Timestamp } from '@angular/fire/firestore'
import { Subscription } from 'rxjs'
import { AdminService } from '../../../services/admin.service'
import { ToastrService } from 'ngx-toastr'

// Collection name kept in sync with FirestoreStructure.OMNISEND_ERROR_LOGS in
// parkupp-model. Inlined here because the dev @parkupp/core symlink doesn't
// always pick up newly added path helpers without a watcher restart.
const OMNISEND_ERROR_LOGS_COLLECTION = 'omnisendErrorLogs'

interface OmnisendErrorRow {
    $key: string
    timestamp?: Timestamp
    method: string
    url: string
    statusCode?: number
    errorMessage?: string
    responseBody?: string
    requestBodyPreview?: string
    uid?: string
    email?: string
    contactId?: string
    source?: string
    expanded?: boolean
}

@Component({
    selector: 'app-admin-omnisend-errors',
    templateUrl: './admin-omnisend-errors.component.html',
    styleUrls: ['./admin-omnisend-errors.component.scss'],
})
export class AdminOmnisendErrorsComponent implements OnInit, OnDestroy {
    rows: OmnisendErrorRow[] = []
    statusFilter: '' | '4xx' | '5xx' | string = ''
    pageSize = 100

    private sub?: Subscription

    constructor(private adminSvc: AdminService, private toastr: ToastrService) {}

    ngOnInit() {
        this.subscribe()
    }

    ngOnDestroy() {
        this.sub?.unsubscribe()
    }

    subscribe() {
        this.sub?.unsubscribe()

        const ref = query(collection(this.adminSvc.firestore, OMNISEND_ERROR_LOGS_COLLECTION), orderBy('timestamp', 'desc'), limit(this.pageSize))

        this.sub = collectionData(ref, { idField: '$key' }).subscribe((data: any[]) => {
            this.rows = data.map((d) => ({ ...d, expanded: false }))
        })
    }

    onPageSizeChange(value: number) {
        this.pageSize = value
        this.subscribe()
    }

    statusBadgeClass(status?: number): string {
        if (!status) return 'bg-secondary'
        if (status >= 500) return 'bg-danger'
        if (status === 429) return 'bg-warning'
        if (status >= 400) return 'bg-warning'
        return 'bg-info'
    }

    toggleRow(row: OmnisendErrorRow) {
        row.expanded = !row.expanded
    }

    copyPayload(row: OmnisendErrorRow, kind: 'request' | 'response') {
        const value = kind === 'request' ? row.requestBodyPreview : row.responseBody
        if (!value) return
        navigator.clipboard
            .writeText(value)
            .then(() => this.toastr.success(`Copied ${kind} payload`))
            .catch(() => this.toastr.error(`Failed to copy ${kind} payload`))
    }

    timestampMoment(row: OmnisendErrorRow): Date | null {
        const ts = row.timestamp as any
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        if (ts.seconds) return new Date(ts.seconds * 1000)
        return new Date(ts)
    }
}
